import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';

// Make a match (Match queue, owner 2026-10-01): the coordinator picks the
// players and their teams — a challenge, a rematch, players who asked to face
// each other — and puts the match at the front of the queue or at the end of
// what is queued now. It counts like any game; everyone keeps their other
// queued games. The queue never skips a match whose player is still in a
// game, so the console says when this one will wait.

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === 'string' ? d : null))) || fallback;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
// free (not queued) → free but queued → in a game → paused / away
const rank = (p) => (p.status === 'free' ? (p.next_position ? 1 : 0) : p.status === 'on_court' ? 2 : 3);
const C = {
  surface: '#0E0708', line: '#2E161B', lineStrong: '#3A1A20', amber: '#F59E0B',
  sheet: 'linear-gradient(180deg, #170B0E 0%, #100809 100%)',
};
const warnStyle = { color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' };

function Tag({ p }) {
  const look = p.status === 'on_court' ? ['#FF6B76', '#E6394618', '#E6394644', p.court_name ? `ON ${p.court_name.toUpperCase()}` : 'IN A GAME']
    : p.status === 'paused' ? ['#9A9A9A', '#FFFFFF08', '#2F2F2F', 'PAUSED']
      : p.status === 'flagged' ? ['#9A9A9A', '#FFFFFF08', '#2F2F2F', 'AWAY']
        : p.next_position ? ['#FCD34D', '#F59E0B14', '#F59E0B44', `UP NEXT #${p.next_position}`]
          : ['#4ADE80', '#22C55E14', '#22C55E44', 'FREE'];
  return (
    <span className="ml-1.5 text-[9.5px] font-extrabold rounded px-1.5 py-0.5 whitespace-nowrap align-[1px]"
      style={{ color: look[0], background: look[1], border: `1px solid ${look[2]}`, letterSpacing: '0.05em' }}>{look[3]}</span>
  );
}

function Level({ v }) {
  return (
    <span className="text-xs px-1.5 py-px rounded shrink-0 tabular-nums"
      style={{ fontFamily: 'Oswald, sans-serif', color: '#D4D4D4', background: '#0A0A0A', border: '1px solid #262626' }}>
      {v == null ? '—' : Number(v).toFixed(1)}
    </span>
  );
}

export default function MakeMatchModal({ sessionId, divisions = [], defaultDivisionId, requirePin, clearPin, onClose, onDone }) {
  const [did, setDid] = useState(defaultDivisionId || divisions[0]?.id);
  const [opts, setOpts] = useState(null);
  const [err, setErr] = useState('');
  const [seats, setSeats] = useState({});
  const [active, setActive] = useState('a0');
  const [q, setQ] = useState('');
  const [place, setPlace] = useState('end');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setOpts(null);
    try {
      const r = await sportsApi.get(`/sports/open-play/${sessionId}/custom-match/options`, { params: { division_id: did } });
      setOpts(r.data);
      // Front is a deliberate pick — default to the end of the current set (owner 2026-10-01).
      setPlace(r.data?.sets?.length ? `set:${r.data.sets[0].set_number}` : 'end');
      setErr('');
    } catch (e) {
      setErr(errMsg(e, 'Could not load the players'));
    }
  }, [sessionId, did]);
  useEffect(() => { load(); setSeats({}); setActive('a0'); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const per = opts?.per_team || 2;
  const keys = useMemo(() => [...Array(per)].flatMap((_, i) => [`a${i}`, `b${i}`]).sort(), [per]);
  const players = opts?.players || [];
  const byId = Object.fromEntries(players.map(p => [p.id, p]));
  const taken = Object.values(seats).filter(Boolean);
  const full = keys.every(k => seats[k]);
  const team = (t) => keys.filter(k => k[0] === t).map(k => seats[k]).filter(Boolean);
  const sum = (ids) => ids.reduce((s, id) => s + Number(byId[id]?.level || 0), 0);

  const pick = (pid) => {
    const next = { ...seats, [active]: pid };
    setSeats(next);
    setQ('');
    setActive(keys.find(k => !next[k]) || null);
  };
  const tapSeat = (k) => {
    if (seats[k]) { const next = { ...seats }; delete next[k]; setSeats(next); }
    setActive(k);
  };

  const list = players
    .filter(p => !taken.includes(p.id))
    .filter(p => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((x, y) => (rank(x) - rank(y)) || (x.games - y.games) || x.name.localeCompare(y.name));

  const onCourt = taken.filter(id => byId[id]?.status === 'on_court');
  const queued = opts?.queued || 0;
  const sets = opts?.sets || [];
  const setNo = place.startsWith('set:') ? Number(place.slice(4)) : null;
  // queue place of the match right before this one (0 = it goes first)
  const before = place === 'front' ? 0 : place === 'end' ? queued
    : sets.filter(x => x.set_number <= setNo).reduce((t, x) => t + x.matches, 0);
  const neighbours = taken.filter(id => {
    const n = byId[id]?.next_position;
    return n && (before === 0 ? n === 1 : n === before);
  });

  const submit = async () => {
    const pin = await requirePin();
    if (pin === null) return;
    setBusy(true);
    try {
      const r = await sportsApi.post(`/sports/open-play/${sessionId}/custom-match`, {
        division_id: did, place: setNo && sets[sets.length - 1]?.set_number === setNo ? 'end' : place, pin,
        team_a: team('a'), team_b: team('b'),
      });
      toast.success(r.data?.started ? 'Custom match started on a free court'
        : `Custom match added at #${r.data?.position}${r.data?.position === 1 ? ' — plays on the next free court' : ''}`);
      onDone();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      setErr(errMsg(e, 'Could not add the match'));
      if (e.response?.status === 409) { setSeats({}); setActive('a0'); load(); }
    } finally { setBusy(false); }
  };

  const seatBtn = (k) => {
    const p = byId[seats[k]];
    const on = active === k;
    return (
      <button key={k} type="button" data-testid={`make-seat-${k}`} onClick={() => tapSeat(k)}
        className="block w-full text-left px-3 py-2.5"
        style={{ minHeight: 56, background: on ? '#F59E0B12' : C.surface, boxShadow: on ? `inset 3px 0 0 ${C.amber}` : 'none', borderTop: k.endsWith('0') ? 0 : `1px solid ${C.line}` }}>
        {p ? (
          <>
            <b className="text-sm" style={{ color: '#fff' }}>{p.name}</b> <Level v={p.level} /><Tag p={p} />
            <span className="block text-[11.5px] mt-0.5" style={{ color: '#8A8A8A' }}>{plural(p.games, 'game', 'games')} · tap to remove</span>
          </>
        ) : (
          <span className="text-[13px] font-bold" style={{ color: on ? '#FCD34D' : '#6A6A6A' }}>{on ? 'Pick a player below ↓' : '＋ Add player'}</span>
        )}
      </button>
    );
  };
  const teamCol = (t, label, color) => (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
      <div className="flex justify-between px-3 py-2 text-[13px] uppercase" style={{ fontFamily: 'Oswald, sans-serif', color, background: `${color}0D`, borderBottom: `1px solid ${C.line}`, letterSpacing: 0.5 }}>
        {label}<span style={{ fontFamily: 'Inter, sans-serif', color: '#8A8A8A', fontWeight: 700 }}>{team(t).length ? sum(team(t)).toFixed(1) : '—'}</span>
      </div>
      {keys.filter(k => k[0] === t).map(seatBtn)}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" data-testid="make-match-modal"
      style={{ background: 'rgba(0,0,0,0.78)' }} onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="make-title"
        className="w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-4 sm:p-5"
        style={{ background: C.sheet, border: `1px solid ${C.lineStrong}`,
                 boxShadow: '0 30px 80px #000, 0 0 0 1px rgba(230,57,70,0.12), 0 0 60px -20px rgba(230,57,70,0.35)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 id="make-title" className="text-lg font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>Make a match</h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm mt-1 mb-3" style={{ color: '#A3A3A3' }}>
          Pick the players and their teams — for a rematch, a challenge, or players who asked to play each other. Tap a seat, then a name.
        </p>
        {divisions.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {divisions.map(d => (
              <button key={d.id} type="button" onClick={() => setDid(d.id)} data-testid={`make-div-${d.id}`}
                className="px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: did === d.id ? '#F59E0B14' : C.surface, color: did === d.id ? '#FCD34D' : '#BDBDBD', border: `1px solid ${did === d.id ? '#F59E0B66' : C.line}` }}>
                {d.name}
              </button>
            ))}
          </div>
        )}
        {!opts && !err && <p className="text-sm py-6 text-center" style={{ color: '#737373' }}>Loading players…</p>}
        {opts && (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              {teamCol('a', 'Team A', '#FF6B76')}
              {teamCol('b', 'Team B', '#6EA8FF')}
            </div>
            {full && (() => {
              const A = sum(team('a')); const B = sum(team('b')); const d = Math.abs(A - B);
              return (
                <div className="flex flex-wrap items-center gap-2.5 rounded-xl px-3 py-2.5 text-[12.5px] mt-3" style={{ background: C.surface, border: `1px solid ${C.line}`, color: '#BDBDBD' }}>
                  Skill level <b style={{ color: '#fff', fontFamily: 'Oswald, sans-serif', fontSize: 15 }}>{A.toFixed(1)}</b> vs <b style={{ color: '#fff', fontFamily: 'Oswald, sans-serif', fontSize: 15 }}>{B.toFixed(1)}</b>
                  <span className="ml-auto font-extrabold" style={{ color: d <= 0.5 ? '#4ADE80' : d <= 1 ? '#A3E635' : '#FCD34D' }}>{d <= 0.5 ? 'Even' : d <= 1 ? 'Close' : 'Uneven — your call'}</span>
                </div>
              );
            })()}
            {active && !seats[active] && (
              <div className="mt-3">
                <div className="relative mb-2.5">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] pointer-events-none" style={{ color: C.amber }} />
                  <input data-testid="make-search" value={q} onChange={e => setQ(e.target.value)} autoComplete="off"
                    placeholder="Search players in this event…"
                    className="w-full rounded-xl pl-11 pr-3.5 py-3 text-[15px] font-semibold outline-none placeholder:text-[#B5B5B5] placeholder:font-medium"
                    style={{ minHeight: 48, background: '#1C1013', color: '#fff', border: '1.5px solid #F59E0B70',
                             boxShadow: '0 0 0 4px #F59E0B10, 0 6px 18px -8px rgba(245,158,11,0.45)' }} />
                </div>
                <div data-testid="make-list" className="rounded-xl overflow-hidden max-h-72 overflow-y-auto" style={{ border: `1px solid ${C.line}` }}>
                  {list.length ? list.map((p, i) => {
                    const disabled = p.status === 'paused' || p.status === 'flagged';
                    return (
                      <button key={p.id} type="button" data-testid={`make-pick-${p.id}`} disabled={disabled} onClick={() => pick(p.id)}
                        className="w-full grid items-center gap-2.5 text-left px-3 py-2.5 hover:bg-[#1A0D10] disabled:hover:bg-transparent"
                        style={{ gridTemplateColumns: 'minmax(0,1fr) auto', background: C.surface, borderTop: i ? `1px solid ${C.line}` : 0, opacity: disabled ? 0.38 : 1 }}>
                        <span className="min-w-0">
                          <span className="text-sm font-bold" style={{ color: '#E5E5E5' }}>{p.name}</span><Tag p={p} />
                          <span className="block text-xs mt-0.5" style={{ color: '#8A8A8A' }}>
                            {plural(p.games, 'game', 'games')} · {p.status === 'on_court' ? 'the match waits until they finish'
                              : p.next_position ? `next game #${p.next_position}` : 'not in the queue'}
                          </span>
                        </span>
                        <Level v={p.level} />
                      </button>
                    );
                  }) : <p className="px-3 py-2.5 text-sm" style={{ background: C.surface, color: '#777' }}>{q.trim() ? `No player matches “${q.trim()}”` : 'Everyone is already picked.'}</p>}
                </div>
              </div>
            )}

            <p className="text-[10.5px] font-extrabold uppercase mt-4 mb-2" style={{ color: '#777', letterSpacing: '0.12em' }}>Where in the queue?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[['front', 'Front of the queue', 'Plays on the next free court.'],
                ...(sets.length ? sets.map((x, i) => [`set:${x.set_number}`, `End of Set ${x.set_number}`,
                  i < sets.length - 1
                    ? `After its ${plural(x.matches, 'match', 'matches')}, before Set ${sets[i + 1].set_number}.`
                    : `After its ${plural(x.matches, 'match', 'matches')} — the end of the queue.`])
                  : [['end', 'End of the queue', 'The queue is empty — it plays next.']])]
                .map(([k, t, hint]) => (
                  <button key={k} type="button" data-testid={`make-place-${k}`} onClick={() => setPlace(k)}
                    className="text-left rounded-xl px-3 py-2.5"
                    style={{ background: place === k ? '#F59E0B0D' : C.surface, border: `1px solid ${place === k ? C.amber : C.line}`, boxShadow: place === k ? `inset 0 0 0 1px ${C.amber}` : 'none' }}>
                    <b className="block text-[13.5px]" style={{ color: '#fff' }}>{t}</b>
                    <span className="text-xs" style={{ color: '#8A8A8A' }}>{hint}</span>
                  </button>
                ))}
            </div>
            {full && onCourt.length > 0 && (
              <p data-testid="make-oncourt-warning" className="text-xs mt-3 px-3 py-2.5 rounded-lg leading-relaxed" style={warnStyle}>
                <b>{onCourt.map(id => `${byId[id].name} (${byId[id].court_name || 'in a game'})`).join(', ')}</b> {onCourt.length === 1 ? 'is' : 'are'} still playing.
                {before === 0 || !queued
                  ? ' This match waits until that game ends — the next match does not jump ahead, so a free court stays empty until then.'
                  : ' If they are still playing when this match reaches the front, it waits for them and a free court stays empty until then.'}
              </p>
            )}
            {full && neighbours.length > 0 && (
              <p className="text-xs mt-3 px-3 py-2.5 rounded-lg leading-relaxed" style={warnStyle}>
                <b>{neighbours.map(id => byId[id].name).join(', ')}</b> also {neighbours.length === 1 ? 'plays' : 'play'} in the match {before === 0 ? 'right after' : 'right before'} this one — two games in a row. Their other games stay.
              </p>
            )}
            {full && (
              <p className="text-[11.5px] mt-3 leading-relaxed" style={{ color: '#777' }}>
                It counts like any game (wins, losses, games played). Everyone keeps their other queued games — the rotation evens out the extra game later.
              </p>
            )}
          </>
        )}
        {err && <p data-testid="make-error" className="text-xs mt-3 px-3 py-2 rounded-lg" style={{ color: '#FCA5A5', background: '#EF44441A', border: '1px solid #EF444440' }}>{err}</p>}
        <div className="flex gap-2 justify-end sticky bottom-0 pt-3 mt-3" style={{ background: '#100809' }}>
          <button type="button" data-testid="make-cancel" onClick={onClose} disabled={busy}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-bold" style={{ color: '#E5E5E5', border: `1px solid ${C.lineStrong}`, background: '#120A0C' }}>
            Cancel
          </button>
          <button type="button" data-testid="make-go" disabled={busy || !full} onClick={submit}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-black uppercase"
            style={{ background: C.amber, color: '#111', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5,
                     opacity: busy || !full ? 0.45 : 1, boxShadow: busy || !full ? 'none' : '0 6px 22px -6px rgba(245,158,11,0.75)' }}>
            {busy ? 'Adding…' : full ? 'Add match'
              : `Pick ${keys.length - taken.length} more player${keys.length - taken.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
